import type {ReactNode, Ref} from 'react'

interface DesignerFrameProps {
    children: ReactNode
    /**
     * Designer chrome height — accepts a CSS length (e.g. `"80vh"`, `600`).
     * Applied as an inline `style={{height}}` on the outer div. Necessary because
     * the value is measured at runtime or bound to viewport units and cannot be
     * expressed as a Tailwind class.
     */
    height?: string | number
    innerRef?: Ref<HTMLDivElement>
}

/**
 * Full-height vertical shell for the form-layout designer. Consumer composes
 * children in order: toolbar, info bar, main workspace, footer action bar.
 */
export function DesignerFrame({children, height, innerRef}: DesignerFrameProps) {
    return (
        <div
            className="flex flex-col"
            style={height !== undefined ? {height} : undefined}
            ref={innerRef}
        >
            {children}
        </div>
    )
}

interface DesignerInfoBarProps {
    children: ReactNode
}

/** Used-on info bar rendered just below the designer toolbar. */
export function DesignerInfoBar({children}: DesignerInfoBarProps) {
    return (
        <div className="flex items-center gap-2 border-b bg-muted/30 px-4 py-1.5 text-xs text-muted-foreground">
            {children}
        </div>
    )
}

interface DesignerWorkspaceProps {
    children: ReactNode
}

/** Horizontal split (palette + canvas) that fills remaining vertical space. */
export function DesignerWorkspace({children}: DesignerWorkspaceProps) {
    return (
        <div className="flex flex-1 min-h-0 overflow-hidden">
            {children}
        </div>
    )
}

interface DesignerPaletteColumnProps {
    children: ReactNode
}

/** Left-hand palette column — fixed 16rem wide, right-bordered, scrollable. */
export function DesignerPaletteColumn({children}: DesignerPaletteColumnProps) {
    return (
        <div className="w-64 shrink-0 overflow-y-auto border-r bg-muted/30">
            {children}
        </div>
    )
}

interface DesignerCanvasColumnProps {
    children: ReactNode
}

/** Main canvas column — fills remaining horizontal space, vertical scroll, inner padding. */
export function DesignerCanvasColumn({children}: DesignerCanvasColumnProps) {
    return (
        <div className="flex flex-1 flex-col overflow-y-auto p-4">
            {children}
        </div>
    )
}

interface DesignerActionBarProps {
    children: ReactNode
}

/** Top-bordered footer row where the designer's cancel/save buttons live. */
export function DesignerActionBar({children}: DesignerActionBarProps) {
    return (
        <div className="border-t bg-background px-4 py-3">
            {children}
        </div>
    )
}
